import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DotLottleLoader } from '../../../components/Loader';
import axiosInstant from '../../../api';
import { useUser } from '../../../hooks/useUser';
import { isBrand, isRMN } from '../../../utils';
import { getVideoEndpoint } from '../../../api/endpoints';
import { IVideo } from '../../admin/videos/hooks/useFetchVideos';
import { VideoCard } from '../../../components/videoSection/VideoCard';
import { UploadableVideoCard } from '../../../components/videoSection/VideoUpload';
import { ReactComponent as AddIcon } from '../../../assets/svg/add-icon.svg';
import { ReactComponent as NoProjectsIcon } from '../../../assets/svg/no-projects.svg';
import { usePortalPath } from '../../../hooks/usePortalPath'
import { useWorkspaceBrandId } from '../../../hooks/useWorkspaceBrandId';

interface VideosProps {
  limit?: number;
  onCount?: (count: number) => void;
}

const Videos: React.FC<VideosProps> = ({ limit = 8, onCount }) => {
  const navigate = useNavigate();
  const { p } = usePortalPath();
  const workspaceBrandId = useWorkspaceBrandId();
  const { brand, user, role } = useUser();
  const [videos, setVideos] = useState<IVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const fetchVideos = async () => {
    try {
      setLoading(true);

      const brandId = workspaceBrandId || (brand ? brand[0].id : 0);
      const response = await axiosInstant.get(
        getVideoEndpoint(brandId.toString(), 'TDC', {
          pagination: { offset: 0, limit: limit },
        }),
      );

      const { data } = response;
      if (data.rows && data.rows.length > 0) {
        setVideos(data.rows);
        setTotalCount(data.count);
        onCount?.(data.count);
      } else {
        setVideos([]);
        setTotalCount(0);
        onCount?.(0);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      setVideos([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (brand || workspaceBrandId) {
      fetchVideos();
    }
  }, [limit, brand, workspaceBrandId]);

  const handleViewAll = () => {
    navigate(p('/videos/tdc'));
  };

  const handleVideoClick = (video: IVideo) => {
    navigate(p(`/videos/tdc?limit=20&video=${video.id}`));
  };

  const handleNewOrder = () => {
    navigate(p('/video-production/orders?create=new'));
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-full min-h-0">
        <DotLottleLoader fill={false} />
      </Box>
    );
  }

  if (videos.length === 0) {
    return (
      <Box className="flex flex-col h-full min-h-0">
        <Box className="flex items-center justify-between mb-4 flex-shrink-0">
          <Typography
            sx={{
              color: 'var(--text)',
              fontFamily: 'CircularStd',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: 'normal',
            }}
          >
            Videos
          </Typography>
        </Box>
        <Box
          className="flex-1 flex flex-col items-center justify-center w-full"
          sx={{ gap: '21px' }}
        >
          <NoProjectsIcon />
          <Typography
            sx={{
              color: 'var(--Base-600, #727272)',
              fontFamily: 'CircularStd',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 450,
              lineHeight: 'normal',
              textAlign: 'center',
            }}
          >
            You have no videos available
          </Typography>
          <Button
            onClick={handleNewOrder}
            sx={{
              backgroundColor: 'var(--accent)',
              color: 'var(--Base-0, #FFF)',
              fontFamily: 'CircularStd',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 900,
              lineHeight: 'normal',
              textTransform: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              alignSelf: 'center',
              '&:hover': {
                backgroundColor: '#8A6D41',
              },
            }}
          >
            <AddIcon />
            {isBrand(role) || isRMN(user) ? 'Request A Quote' : 'New Order'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="flex flex-col h-full min-h-0">
      {/* Header */}
      <Box className="flex items-center justify-between mb-4 flex-shrink-0">
        <Typography
          sx={{
            color: 'var(--text)',
            fontFamily: 'CircularStd',
            fontSize: { xs: '15px', md: '18px' },
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
          }}
        >
          Videos ({totalCount})
        </Typography>
        <Button
          onClick={handleViewAll}
          sx={{
            color: 'var(--accent-text)',
            fontFamily: 'CircularStd',
            fontSize: { xs: '12px', md: '14px' },
            fontWeight: 450,
            textTransform: 'none',
            padding: 0,
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          View All
        </Button>
      </Box>

      {/* Videos List - Horizontal Scroll */}
      <Box className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden">
        <Box className="flex gap-4 pb-2 max-md:gap-3" style={{ width: 'max-content' }}>
          {videos.map((video) => (
            <div
              key={video.id}
              className="flex-shrink-0 max-md:!w-[220px]"
              style={{ width: '296px' }}
            >
              {/* Drag-only, matching the pre-redesign card: VideoCardV2 mounted its
                  dropzone on role alone and rendered no visible control. The compact
                  tile has no room for a button and the redesign already dropped its
                  kebab, so uploadSlot is deliberately not passed. */}
              <UploadableVideoCard video={video} project={(video as any).project} refetch={() => undefined}>
                {() => (
                  <VideoCard
                    video={video}
                    variant="compact"
                    showMenu={false}
                    onOpen={() => handleVideoClick(video)}
                  />
                )}
              </UploadableVideoCard>
            </div>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Videos;
